import React from "react";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "../patients.css";
import "./PatientProfile.css";
import "./PatientProfilePrint.css";

const LIFE_ANAMNESIS_FIELD_LABELS = {
  general_condition: "anamnesis.life.generalCondition",
  operations_injuries: "anamnesis.life.operationsInjuries",
  medications: "anamnesis.life.medications",
};

const SOMATIC_DISEASES_FIELD_LABELS = {
  chronic_diseases: "anamnesis.somatic.chronicDiseases",
};

const ALLERGY_ANAMNESIS_KEYS = [
  "medications",
  "cosmetics",
  "food",
  "household",
  "plants",
  "sun",
  "cold",
];

const ALLERGY_ANAMNESIS_FIELD_LABELS = {
  medications: "anamnesis.allergy.medications",
  cosmetics: "anamnesis.allergy.cosmetics",
  food: "anamnesis.allergy.food",
  household: "anamnesis.allergy.household",
  plants: "anamnesis.allergy.plants",
  sun: "anamnesis.allergy.sun",
  cold: "anamnesis.allergy.cold",
};

const EPID_ANAMNESIS_KEYS = [
  "hepatitis",
  "hiv",
  "syphilis",
  "tuberculosis",
  "herpes",
  "other_infectious",
  "std",
];

const EPID_ANAMNESIS_FIELD_LABELS = {
  hepatitis: "anamnesis.epid.hepatitis",
  hiv: "anamnesis.epid.hiv",
  syphilis: "anamnesis.epid.syphilis",
  tuberculosis: "anamnesis.epid.tuberculosis",
  herpes: "anamnesis.epid.herpes",
  other_infectious: "anamnesis.epid.otherInfectious",
  std: "anamnesis.epid.std",
};

const NUTRITION_FOOD_CHARACTER_KEYS = [
  "balanced",
  "protein",
  "vegetarian",
  "high_fat",
  "fast_food",
  "high_carb",
  "spicy",
  "smoked",
  "salty",
  "sweet",
  "soda",
];

const NUTRITION_FOOD_CHARACTER_LABELS = {
  balanced: "anamnesis.nutrition.food.balanced",
  protein: "anamnesis.nutrition.food.protein",
  vegetarian: "anamnesis.nutrition.food.vegetarian",
  high_fat: "anamnesis.nutrition.food.highFat",
  fast_food: "anamnesis.nutrition.food.fastFood",
  high_carb: "anamnesis.nutrition.food.highCarb",
  spicy: "anamnesis.nutrition.food.spicy",
  smoked: "anamnesis.nutrition.food.smoked",
  salty: "anamnesis.nutrition.food.salty",
  sweet: "anamnesis.nutrition.food.sweet",
  soda: "anamnesis.nutrition.food.soda",
};

const DAILY_ROUTINE_SLEEP_CHARACTER_KEYS = ["calm", "restless", "dreams"];
const DAILY_ROUTINE_SLEEP_CHARACTER_LABELS = {
  calm: "anamnesis.routine.sleep.calm",
  restless: "anamnesis.routine.sleep.restless",
  dreams: "anamnesis.routine.sleep.dreams",
};

const DAILY_ROUTINE_AWAKENING_KEYS = ["full_rest", "fatigue", "spontaneous"];
const DAILY_ROUTINE_AWAKENING_LABELS = {
  full_rest: "anamnesis.routine.wake.fullRest",
  fatigue: "anamnesis.routine.wake.fatigue",
  spontaneous: "anamnesis.routine.wake.spontaneous",
};

const DAILY_ROUTINE_ACTIVITY_KEYS = ["very_low", "low", "medium", "high"];
const DAILY_ROUTINE_ACTIVITY_LABELS = {
  very_low: "anamnesis.routine.activity.veryLow",
  low: "anamnesis.routine.activity.low",
  medium: "anamnesis.routine.activity.medium",
  high: "anamnesis.routine.activity.high",
};

const ANTHROPOMETRY_BODY_TYPE_KEYS = ["normosthenic", "hypersthenic", "asthenic"];
const ANTHROPOMETRY_BODY_TYPE_LABELS = {
  normosthenic: "anamnesis.anthropometry.bodyTypeOptions.normosthenic",
  hypersthenic: "anamnesis.anthropometry.bodyTypeOptions.hypersthenic",
  asthenic: "anamnesis.anthropometry.bodyTypeOptions.asthenic",
};

const ANTHROPOMETRY_REDISTRIBUTION_KEYS = ["android", "gynoid", "mixed"];
const ANTHROPOMETRY_REDISTRIBUTION_LABELS = {
  android: "anamnesis.anthropometry.redistributionOptions.android",
  gynoid: "anamnesis.anthropometry.redistributionOptions.gynoid",
  mixed: "anamnesis.anthropometry.redistributionOptions.mixed",
};

const displayLinedValue = (value) => (!value && value !== 0 ? "" : value);

const LinedValue = ({
  as: Tag = "div",
  className = "patient-inline-value",
  value,
  alwaysLined = false,
}) => {
  const text = displayLinedValue(value);
  const filled = text !== "" && text != null;
  const lined = alwaysLined || filled;
  return <Tag className={lined ? `${className} is-filled` : className}>{text}</Tag>;
};

const noop = () => {};

/**
 * Печатный вид анамнеза в том же layout, что PatientProfile (режим просмотра).
 * Секции: АНАМНЕЗ ЖИЗНИ … АНТРОПОМЕТРИЧЕСКИЕ ПОКАЗАТЕЛИ.
 */
function PatientProfilePrintFormView({
  complaints,
  lifeAnamnesis,
  somaticDiseases,
  gynAnamnesis,
  gynecologicalHistory,
  allergyAnamnesis,
  epidAnamnesis,
  nutritionAnamnesis,
  dailyRoutine,
  anthropometry,
  bmiText,
}) {
  const { t } = useTranslation("patient");

  const foodCharacter = nutritionAnamnesis?.food_character || [];
  const sleepCharacter = dailyRoutine?.sleep_character || [];
  const awakening = dailyRoutine?.awakening || [];
  const awakeningTime = dailyRoutine?.awakening_time ?? "";

  return (
    <div className="patient-profile patient-profile-print-form-view">
      {/* Анамнез жизни */}
      <div className="patient-life-anamnesis-block">
        <Row className="mb-2">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title patient-profile-group-title--print-form">{t("profile.print.formSections.life")}</div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">{t("anamnesis.labels.generalComplaints")}</span>
                <LinedValue value={complaints} />
              </div>
            </div>
          </Col>
        </Row>

        {Object.keys(LIFE_ANAMNESIS_FIELD_LABELS)
          .filter((fieldKey) => fieldKey !== "medications")
          .map((fieldKey) => (
            <Row className="mb-1" key={fieldKey}>
              <Col md={12}>
                <div className="patient-form-group">
                  <div className="patient-inline-field">
                    <span className="patient-inline-label">
                      {t(LIFE_ANAMNESIS_FIELD_LABELS[fieldKey])}
                    </span>
                    <LinedValue value={lifeAnamnesis?.[fieldKey]} />
                  </div>
                </div>
              </Col>
            </Row>
          ))}

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field patient-inline-field--medications">
                <span className="patient-inline-label">
                  {t(LIFE_ANAMNESIS_FIELD_LABELS.medications)}
                </span>
                <div className="patient-medications-field">
                  <LinedValue
                    className="patient-inline-value patient-medications-value"
                    value={lifeAnamnesis?.medications}
                  />
                  <div className="patient-medications-hint">
                    {t("anamnesis.life.medicationsHint")}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Общие соматические заболевания */}
      <div className="patient-life-anamnesis-block patient-somatic-diseases-block">
        <Row className="mb-2">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title patient-profile-group-title--print-form">
                {t("profile.print.formSections.somatic")}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-somatic-field-label">
                {t(SOMATIC_DISEASES_FIELD_LABELS.chronic_diseases)}
              </div>
              <LinedValue
                className="patient-lined-value patient-lined-value--3"
                value={somaticDiseases?.chronic_diseases}
              />
            </div>
          </Col>
        </Row>
      </div>

      {/* Эндокринный статус */}
      <div className="patient-life-anamnesis-block patient-somatic-diseases-block">
        <Row className="mb-2">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title patient-profile-group-title--print-form">
                {t("anamnesis.somatic.endocrineStatus")}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <LinedValue value={somaticDiseases?.endocrine_status} />
            </div>
          </Col>
        </Row>
      </div>

      {/* Гинекологический анамнез */}
      <div className="patient-life-anamnesis-block patient-gyn-anamnesis-block">
        <Row className="mb-2">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title patient-profile-group-title--print-form">{t("profile.print.formSections.gyn")}</div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-gyn-inline-row">
                <span>{t("anamnesis.gyn.menstruationFrom")} </span>
                <LinedValue
                  as="span"
                  className="patient-gyn-short-value"
                  value={gynAnamnesis?.menstruation_age}
                />
                <span>{t("anamnesis.gyn.yearsShort")}</span>
                <label className="patient-gyn-check">
                  <input
                    type="checkbox"
                    checked={gynAnamnesis?.regularity === "regular"}
                    onChange={noop}
                  />
                  {t("anamnesis.gyn.regular")}
                </label>
                <label className="patient-gyn-check">
                  <input
                    type="checkbox"
                    checked={gynAnamnesis?.regularity === "irregular"}
                    onChange={noop}
                  />
                  {t("anamnesis.gyn.irregular")}
                </label>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">{t("anamnesis.gyn.cycleDuration")}</span>
                <LinedValue value={gynAnamnesis?.cycle_duration} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">{t("anamnesis.gyn.pms")}</span>
                <LinedValue value={gynAnamnesis?.pms} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-gyn-inline-row patient-gyn-pregnancy-row">
                <span>{t("anamnesis.gyn.pregnancyTotal")}</span>
                <LinedValue
                  as="span"
                  className="patient-gyn-tiny-value"
                  value={gynAnamnesis?.pregnancy_r}
                />
                <span>{t("anamnesis.gyn.pregnancyA")}</span>
                <LinedValue
                  as="span"
                  className="patient-gyn-tiny-value"
                  value={gynAnamnesis?.pregnancy_a}
                />
                <span>{t("anamnesis.gyn.pregnancyV")}</span>
                <LinedValue
                  as="span"
                  className="patient-gyn-tiny-value"
                  value={gynAnamnesis?.pregnancy_v}
                />
                <span>)</span>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">
                  {t("anamnesis.gyn.menstrualSymptoms")}
                </span>
                <LinedValue value={gynAnamnesis?.menstrual_symptoms} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-gyn-inline-row">
                <span>{t("anamnesis.gyn.menopauseFrom")}</span>
                <LinedValue
                  as="span"
                  className="patient-gyn-short-value"
                  value={gynAnamnesis?.menopause_age}
                  alwaysLined
                />
                <span>{t("anamnesis.gyn.yearsParen")}</span>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">
                  {t("anamnesis.gyn.hormonalDrugs")}
                </span>
                <LinedValue value={gynAnamnesis?.hormonal_drugs} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">{t("anamnesis.gyn.diseases")}</span>
                <LinedValue value={gynecologicalHistory} />
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Аллергический анамнез */}
      <div className="patient-life-anamnesis-block patient-yesno-anamnesis-block">
        <Row className="mb-0">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title patient-profile-group-title--print-form">{t("profile.print.formSections.allergy")}</div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-yesno-table">
                <div className="patient-yesno-header">
                  <span className="patient-yesno-label-col" />
                  <span className="patient-yesno-check-col">{t("anamnesis.labels.no")}</span>
                  <span className="patient-yesno-check-col">{t("anamnesis.labels.yes")}</span>
                  <span className="patient-yesno-note-col" />
                </div>
                {ALLERGY_ANAMNESIS_KEYS.map((fieldKey) => {
                  const item = allergyAnamnesis?.[fieldKey];
                  return (
                    <div className="patient-yesno-row" key={fieldKey}>
                      <span className="patient-yesno-label-col">
                        {t(ALLERGY_ANAMNESIS_FIELD_LABELS[fieldKey])}
                      </span>
                      <label className="patient-yesno-check-col patient-yesno-check">
                        <input
                          type="checkbox"
                          checked={item?.answer === "no"}
                          onChange={noop}
                        />
                      </label>
                      <label className="patient-yesno-check-col patient-yesno-check">
                        <input
                          type="checkbox"
                          checked={item?.answer === "yes"}
                          onChange={noop}
                        />
                      </label>
                      <LinedValue
                        as="span"
                        className="patient-yesno-note-col patient-yesno-note-value"
                        value={item?.note}
                        alwaysLined
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Эпидемиологический анамнез */}
      <div className="patient-life-anamnesis-block patient-yesno-anamnesis-block">
        <Row className="mb-0">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title patient-profile-group-title--print-form">{t("profile.print.formSections.epid")}</div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-yesno-table">
                <div className="patient-yesno-header">
                  <span className="patient-yesno-label-col" />
                  <span className="patient-yesno-check-col">{t("anamnesis.labels.no")}</span>
                  <span className="patient-yesno-check-col">{t("anamnesis.labels.yes")}</span>
                  <span className="patient-yesno-note-col" />
                </div>
                {EPID_ANAMNESIS_KEYS.map((fieldKey) => {
                  const item = epidAnamnesis?.[fieldKey];
                  return (
                    <div className="patient-yesno-row" key={fieldKey}>
                      <span className="patient-yesno-label-col">
                        {t(EPID_ANAMNESIS_FIELD_LABELS[fieldKey])}
                      </span>
                      <label className="patient-yesno-check-col patient-yesno-check">
                        <input
                          type="checkbox"
                          checked={item?.answer === "no"}
                          onChange={noop}
                        />
                      </label>
                      <label className="patient-yesno-check-col patient-yesno-check">
                        <input
                          type="checkbox"
                          checked={item?.answer === "yes"}
                          onChange={noop}
                        />
                      </label>
                      <LinedValue
                        as="span"
                        className="patient-yesno-note-col patient-yesno-note-value"
                        value={item?.note}
                        alwaysLined
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Стоматологический анамнез (только для печати, без данных) */}
      <div className="patient-life-anamnesis-block patient-dental-anamnesis-block">
        <Row className="mb-2">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title patient-profile-group-title--print-form">
                {t("profile.print.formSections.dental")}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-dental-inline-row">
                <span>{t("profile.print.lastVisit")}</span>
                <LinedValue
                  as="span"
                  className="patient-dental-blank-value"
                  value=""
                  alwaysLined
                />
                <span>{t("profile.print.biteChange")}</span>
                <LinedValue
                  as="span"
                  className="patient-dental-blank-value"
                  value=""
                  alwaysLined
                />
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Характер питания */}
      <div className="patient-life-anamnesis-block patient-nutrition-anamnesis-block">
        <Row className="mb-2">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title patient-profile-group-title--print-form">{t("profile.print.formSections.nutrition")}</div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-nutrition-inline-row patient-nutrition-meal-regime-row">
                <span className="patient-nutrition-row-label">{t("anamnesis.nutrition.mealRegime")}</span>
                <div className="patient-nutrition-checks patient-nutrition-checks--stacked">
                  <label className="patient-nutrition-check">
                    <input
                      type="checkbox"
                      checked={nutritionAnamnesis?.meal_regime === "regular"}
                      onChange={noop}
                    />
                    {t("anamnesis.nutrition.regular")}
                  </label>
                  <label className="patient-nutrition-check">
                    <input
                      type="checkbox"
                      checked={nutritionAnamnesis?.meal_regime === "irregular"}
                      onChange={noop}
                    />
                    {t("anamnesis.nutrition.irregular")}
                  </label>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">{t("anamnesis.nutrition.mealsPerDay")}</span>
                <LinedValue value={nutritionAnamnesis?.meals_per_day} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-nutrition-inline-row patient-nutrition-food-character-row">
                <span className="patient-nutrition-row-label">{t("anamnesis.nutrition.foodCharacter")}</span>
                <div className="patient-nutrition-checks patient-nutrition-checks--two-cols">
                  {NUTRITION_FOOD_CHARACTER_KEYS.map((key) => (
                    <label className="patient-nutrition-check" key={key}>
                      <input
                        type="checkbox"
                        checked={foodCharacter.includes(key)}
                        onChange={noop}
                      />
                      {t(NUTRITION_FOOD_CHARACTER_LABELS[key])}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">
                  {t("anamnesis.nutrition.fluidLiters")}
                </span>
                <LinedValue value={nutritionAnamnesis?.fluid_liters} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-nutrition-inline-row patient-nutrition-habits-row">
                <span className="patient-nutrition-row-label">{t("anamnesis.nutrition.smokingAlcohol")}</span>
                <LinedValue
                  as="span"
                  className="patient-nutrition-habit-value"
                  value={nutritionAnamnesis?.smoking_alcohol}
                  alwaysLined
                />
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Распорядок дня */}
      <div className="patient-life-anamnesis-block patient-nutrition-anamnesis-block">
        <Row className="mb-2">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title patient-profile-group-title--print-form">{t("profile.print.formSections.routine")}</div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">{t("anamnesis.routine.sleepHours")}</span>
                <LinedValue value={dailyRoutine?.sleep_hours} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-nutrition-inline-row patient-nutrition-meal-regime-row">
                <span className="patient-nutrition-row-label">{t("anamnesis.routine.sleepCharacter")}</span>
                <div className="patient-nutrition-checks patient-nutrition-checks--stacked">
                  {DAILY_ROUTINE_SLEEP_CHARACTER_KEYS.map((key) => (
                    <label className="patient-nutrition-check" key={key}>
                      <input
                        type="checkbox"
                        checked={sleepCharacter.includes(key)}
                        onChange={noop}
                      />
                      {t(DAILY_ROUTINE_SLEEP_CHARACTER_LABELS[key])}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-nutrition-inline-row patient-nutrition-meal-regime-row">
                <span className="patient-nutrition-row-label">{t("anamnesis.routine.awakening")}</span>
                <div className="patient-nutrition-checks patient-nutrition-checks--stacked">
                  {DAILY_ROUTINE_AWAKENING_KEYS.map((key) => {
                    if (key === "spontaneous") {
                      return (
                        <div
                          className="patient-nutrition-check patient-nutrition-check--with-hours"
                          key={key}>
                          <label className="patient-nutrition-check">
                            <input
                              type="checkbox"
                              checked={awakening.includes(key)}
                              onChange={noop}
                            />
                            {t("anamnesis.routine.wake.spontaneous")}
                          </label>
                          <LinedValue
                            as="span"
                            className="patient-nutrition-inline-hours-value"
                            value={String(awakeningTime).trim() || null}
                            alwaysLined
                          />
                          <span>{t("anamnesis.labels.hoursShort")}</span>
                        </div>
                      );
                    }
                    return (
                      <label className="patient-nutrition-check" key={key}>
                        <input
                          type="checkbox"
                          checked={awakening.includes(key)}
                          onChange={noop}
                        />
                        {t(DAILY_ROUTINE_AWAKENING_LABELS[key])}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-nutrition-field-label">
                {t("anamnesis.routine.activityTitle")}
              </div>
              <div className="patient-nutrition-checks">
                {DAILY_ROUTINE_ACTIVITY_KEYS.map((key) => (
                  <label className="patient-nutrition-check" key={key}>
                    <input
                      type="checkbox"
                      checked={dailyRoutine?.physical_activity === key}
                      onChange={noop}
                    />
                    {t(DAILY_ROUTINE_ACTIVITY_LABELS[key])}
                  </label>
                ))}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">
                  {t("anamnesis.routine.activityPeak")}:
                </span>
                <LinedValue value={dailyRoutine?.activity_peak} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-nutrition-inline-row">
                <span className="patient-nutrition-row-label">
                  {t("anamnesis.routine.sports")}
                </span>
                <label className="patient-nutrition-check">
                  <input
                    type="checkbox"
                    checked={dailyRoutine?.sports === "no"}
                    onChange={noop}
                  />
                  {t("anamnesis.routine.sportsNo")}
                </label>
                <label className="patient-nutrition-check">
                  <input
                    type="checkbox"
                    checked={dailyRoutine?.sports === "yes"}
                    onChange={noop}
                  />
                  {t("anamnesis.routine.sportsYes")}
                </label>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">
                  {t("anamnesis.routine.stressLevel")}
                </span>
                <LinedValue value={dailyRoutine?.stress_level} />
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Антропометрические показатели */}
      <div className="patient-life-anamnesis-block patient-nutrition-anamnesis-block">
        <Row className="mb-2">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-profile-group-title patient-profile-group-title--print-form">{t("profile.print.formSections.anthropometry")}</div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">{t("anamnesis.anthropometry.weightKg")}</span>
                <LinedValue value={anthropometry?.weight_kg} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">{t("anamnesis.anthropometry.heightCm")}</span>
                <LinedValue value={anthropometry?.height_cm} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-nutrition-inline-row patient-nutrition-meal-regime-row">
                <span className="patient-nutrition-row-label">{t("anamnesis.anthropometry.bodyType")}</span>
                <div className="patient-nutrition-checks">
                  {ANTHROPOMETRY_BODY_TYPE_KEYS.map((key) => (
                    <label className="patient-nutrition-check" key={key}>
                      <input
                        type="checkbox"
                        checked={anthropometry?.body_type === key}
                        onChange={noop}
                      />
                      {t(ANTHROPOMETRY_BODY_TYPE_LABELS[key])}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">
                  {t("anamnesis.anthropometry.fluctuation35")}
                </span>
                <LinedValue value={anthropometry?.weight_fluctuation_3_5_years} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">{t("anamnesis.anthropometry.fluctuation1")}</span>
                <LinedValue value={anthropometry?.weight_fluctuation_1_year} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-inline-field">
                <span className="patient-inline-label">{t("anamnesis.anthropometry.bmi")}</span>
                <LinedValue value={bmiText} />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-1">
          <Col md={12}>
            <div className="patient-form-group">
              <div className="patient-nutrition-inline-row patient-nutrition-meal-regime-row">
                <span className="patient-nutrition-row-label">
                  {t("anamnesis.anthropometry.redistribution")}
                </span>
                <div className="patient-nutrition-checks">
                  {ANTHROPOMETRY_REDISTRIBUTION_KEYS.map((key) => (
                    <label className="patient-nutrition-check" key={key}>
                      <input
                        type="checkbox"
                        checked={anthropometry?.volume_redistribution === key}
                        onChange={noop}
                      />
                      {t(ANTHROPOMETRY_REDISTRIBUTION_LABELS[key])}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className="patient-profile-print-consent">
        <p className="patient-profile-print-consent-text">
          {t("profile.print.consentLead")}{" "}
          <span className="patient-profile-print-consent-fio-line" />
          <br />
          {t("profile.print.consentBody")}
        </p>
        <div className="patient-profile-print-consent-signs">
          <div className="patient-profile-print-consent-sign">
            <span className="patient-profile-print-consent-sign-line" />
            <span className="patient-profile-print-consent-sign-label">
              {t("profile.print.patientSignature")}
            </span>
          </div>
          <div className="patient-profile-print-consent-sign">
            <span className="patient-profile-print-consent-sign-line patient-profile-print-consent-sign-line--wide" />
            <span className="patient-profile-print-consent-sign-label">
              {t("profile.print.signatureDecoding")}
            </span>
          </div>
          <div className="patient-profile-print-consent-sign">
            <span className="patient-profile-print-consent-date">
              {t("profile.print.datePlaceholder")}
            </span>
            <span className="patient-profile-print-consent-sign-label">
              {t("profile.print.dateLabel")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PatientProfilePrintFormView;
